// API-first configuration.
// The frontend talks to the owner's Node + Express + MariaDB backend through
// VITE_PUBLIC_API_URL. When it is empty (this preview environment), every
// screen falls back to the demo data in src/mocks.

const rawApiUrl = (import.meta.env.VITE_PUBLIC_API_URL as string | undefined) ?? "";

export const API_BASE_URL = rawApiUrl.trim().replace(/\/+$/, "");

export const IS_MOCK = API_BASE_URL.length === 0;

// Authentication can be pointed at its own base (e.g. "/api" on the shared
// host, or a full URL). Keeping it separate means enabling real sign-in does
// not require the rest of the data API to exist yet. Falls back to the general
// API base when only that one is configured.
const rawAuthUrl = (import.meta.env.VITE_PUBLIC_AUTH_API_URL as string | undefined) ?? "";

export const AUTH_BASE_URL = (rawAuthUrl.trim() || rawApiUrl.trim()).replace(/\/+$/, "");

/** True when no auth API is configured, so the demo owner login is used. */
export const IS_AUTH_MOCK = AUTH_BASE_URL.length === 0;

export function apiUrl(path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${clean}`;
}

// ---------------------------------------------------------------------------
// Uploads — public base for uploaded media (branding images, listing photos).
// The server stores files under /uploads and may return either an absolute URL
// or a site-relative path such as "/uploads/branding/logo-1a2b.png". When
// VITE_UPLOAD_BASE_URL is set (e.g. https://www.example.com/uploads) every
// stored path is resolved against it, so media keeps working no matter which
// host serves the files.
// ---------------------------------------------------------------------------
const rawUploadUrl = (import.meta.env.VITE_UPLOAD_BASE_URL as string | undefined) ?? "";

export const UPLOAD_BASE_URL = rawUploadUrl.trim().replace(/\/+$/, "");

/**
 * Resolves a stored media path to a browsable URL.
 * - Absolute URLs (http/https) and data:/blob: pass through untouched.
 * - A leading "/uploads/" segment is folded into UPLOAD_BASE_URL so it is not
 *   duplicated (base "…/uploads" + "/branding/x.png" → "…/uploads/branding/x.png").
 * - With no base configured, the path is returned as a same-origin "/uploads/…".
 */
export function uploadUrl(path: string): string {
  const value = (path || "").trim();
  if (!value) return "";
  if (/^(https?:)?\/\//i.test(value) || /^(data|blob):/i.test(value)) return value;
  const clean = `/${value.replace(/^\/+/, "").replace(/^uploads\//i, "")}`;
  return UPLOAD_BASE_URL ? `${UPLOAD_BASE_URL}${clean}` : `/uploads${clean}`;
}

export function authUrl(path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${AUTH_BASE_URL}${clean}`;
}

// ---------------------------------------------------------------------------
// Forms — the site posts to OUR OWN backend endpoints, never to a third-party
// or builder-hosted form service. Leave the base empty to submit to the Node
// API handlers (e.g. /api/forms/contact) on the same origin.
// Set VITE_PUBLIC_FORM_BASE_URL to a full URL when the handlers live elsewhere
// (e.g. https://api.example.com).
// ---------------------------------------------------------------------------
const rawFormUrl = (import.meta.env.VITE_PUBLIC_FORM_BASE_URL as string | undefined) ?? "";

export const FORM_BASE_URL = rawFormUrl.trim().replace(/\/+$/, "");

export function formUrl(path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (!FORM_BASE_URL) return clean;
  // The configured base may already end with the "/forms" segment (e.g.
  // https://www.example.com/forms) while the endpoint paths also start with
  // "/forms" — fold the two so the segment is never doubled.
  const base = FORM_BASE_URL.replace(/\/forms$/i, "");
  return `${base}${clean}`;
}

// ---------------------------------------------------------------------------
// Optional spam protection (Google reCAPTCHA v3 or Cloudflare Turnstile).
// Leave the provider / site key empty to run the forms without a captcha — the
// server side is a no-op in that case. Server secrets live only in PHP config.
// ---------------------------------------------------------------------------
const rawCaptchaProvider = (import.meta.env.VITE_PUBLIC_CAPTCHA_PROVIDER as string | undefined) ?? "";
const rawCaptchaSiteKey = (import.meta.env.VITE_PUBLIC_CAPTCHA_SITE_KEY as string | undefined) ?? "";

export const CAPTCHA_PROVIDER = rawCaptchaProvider.trim().toLowerCase();
export const CAPTCHA_SITE_KEY = rawCaptchaSiteKey.trim();